import React, { useState } from "react";
import classNames from "classnames";
import Button from "@/ui/Button/Button";
import { ChevronIcon } from "@/assets/icons";
import { DateRange } from "@/store/statisticsPeriodStore";
import {
  MONTH_NAMES,
  addDays,
  daysInRange,
  isSameDay,
  pluralDays,
} from "./periodDates";
import cls from "./styles.module.scss";

const WEEK_DAYS = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

interface PeriodCalendarProps {
  today: Date;
  minDate?: Date;
  initialRange: DateRange | null;
  onApply: (range: DateRange) => void;
  // вызывается при любом изменении выбранных дат: сохраняет черновик,
  // запрос уходит только по «Показать» (onApply)
  onChange?: (range: DateRange | null) => void;
  onBack: () => void;
}

export const PeriodCalendar = ({
  today,
  minDate,
  initialRange,
  onApply,
  onChange,
  onBack,
}: PeriodCalendarProps) => {
  const [start, setStart] = useState<Date | null>(initialRange?.from ?? null);
  const [end, setEnd] = useState<Date | null>(initialRange?.to ?? null);
  const [month, setMonth] = useState(() => {
    const base = initialRange?.from ?? today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const isCurrentMonth =
    month.getFullYear() === today.getFullYear() &&
    month.getMonth() === today.getMonth();
  const isMinMonth =
    !!minDate &&
    month.getFullYear() === minDate.getFullYear() &&
    month.getMonth() === minDate.getMonth();

  const shiftMonth = (delta: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));

  // одна выбранная дата = период из одного дня
  const selectRange = (nextStart: Date | null, nextEnd: Date | null) => {
    setStart(nextStart);
    setEnd(nextEnd);
    onChange?.(nextStart ? { from: nextStart, to: nextEnd ?? nextStart } : null);
  };

  const handleDayClick = (day: Date) => {
    if (!start || end) {
      selectRange(day, null);
    } else if (day < start) {
      selectRange(day, start);
    } else {
      selectRange(start, day);
    }
  };

  const handleReset = () => {
    if (!start) {
      onBack();
      return;
    }
    selectRange(null, null);
  };

  const handleApply = () => {
    if (start && end) onApply({ from: start, to: end });
  };

  // сетка начинается с понедельника
  const offset = (month.getDay() + 6) % 7;
  const daysCount = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0
  ).getDate();
  const days = Array.from({ length: daysCount }, (_, i) => addDays(month, i));

  const getDayClass = (day: Date) => {
    if (start && end) {
      if (isSameDay(day, start)) return cls.rangeStart;
      if (isSameDay(day, end)) return cls.rangeEnd;
      if (day > start && day < end) return cls.inRange;
      return undefined;
    }
    if (start && isSameDay(day, start)) {
      return classNames(cls.rangeStart, cls.rangeEnd);
    }
    return undefined;
  };

  let applyLabel = "Выберите даты";
  if (start && end) {
    const count = daysInRange(start, end);
    applyLabel = `Показать за ${count} ${pluralDays(count)}`;
  } else if (start) {
    applyLabel = "Выберите вторую дату";
  }

  return (
    <div className={cls.calendarPad}>
      <div className={cls.calendarTop}>
        <button
          type="button"
          data-trigger="ref.period.previous_month"
          className={cls.calendarNav}
          onClick={() => shiftMonth(-1)}
          disabled={isMinMonth}
          aria-label="Предыдущий месяц"
        >
          <ChevronIcon direction="left" />
        </button>
        <div className={cls.calendarMonth}>
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </div>
        <button
          type="button"
          data-trigger="ref.period.next_month"
          className={cls.calendarNav}
          onClick={() => shiftMonth(1)}
          disabled={isCurrentMonth}
          aria-label="Следующий месяц"
        >
          <ChevronIcon direction="right" />
        </button>
      </div>

      <div className={cls.calendarGrid}>
        {WEEK_DAYS.map((name) => (
          <div key={name} className={cls.weekDay}>
            {name}
          </div>
        ))}
        {Array.from({ length: offset }, (_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {days.map((day) => {
          const isDisabled = day > today || (!!minDate && day < minDate);
          return (
            <button
              key={day.getTime()}
              type="button"
              data-trigger="ref.period.day"
              className={classNames(cls.day, getDayClass(day))}
              disabled={isDisabled}
              onClick={() => handleDayClick(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <div className={cls.calendarFoot}>
        <Button
          trigger="ref.period.apply"
          variant="yellow"
          label={applyLabel}
          disabled={!start || !end}
          onClick={handleApply}
          className={cls.applyButton}
        />
        <Button
          trigger="ref.period.reset"
          variant="white"
          label={start ? "Сбросить" : "Назад"}
          onClick={handleReset}
        />
      </div>
    </div>
  );
};
